import type { MarketMonitorIdentifier, MarketMonitorWrite } from './index';

export const BUSINESS_RELATIONSHIPS = ['owned', 'agency', 'competitor', 'benchmark', 'other'] as const;
export interface MarketBusinessSubject { name: string; relationship: typeof BUSINESS_RELATIONSHIPS[number] }
const record = (value: unknown): value is Record<string, unknown> => value != null && typeof value === 'object' && !Array.isArray(value);
export const normalizeBusinessName = (value: string) => value.normalize('NFKC').trim().replace(/\s+/g, ' ');
export function validBusinessSubject(value: unknown): value is MarketBusinessSubject | null | undefined {
    return value == null || record(value) && Object.keys(value).length === 2 && typeof value.name === 'string'
        && normalizeBusinessName(value.name).length > 0 && normalizeBusinessName(value.name).length <= 200
        && !/[\u0000-\u001f\u007f-\u009f]/.test(normalizeBusinessName(value.name))
        && BUSINESS_RELATIONSHIPS.includes(value.relationship as MarketBusinessSubject['relationship']);
}

/** Namespace-qualified account identity, independent of display names and declarations. */
export function businessIdentifierKey(item: Pick<MarketMonitorIdentifier, 'source_system' | 'value'>): string {
    let value = item.value.trim().replace(/^@/, '');
    try {
        const url = new URL(value);
        const host = item.source_system === 'meta_ad_library' ? 'facebook.com' : `${item.source_system}.com`;
        if (![host, `www.${host}`].includes(url.hostname) || url.protocol !== 'https:' || url.username || url.password || url.port)
            return `${item.source_system}:invalid:${value}`;
        const pageId = item.source_system === 'meta_ad_library' ? url.searchParams.get('view_all_page_id') : null;
        value = pageId || url.pathname.replace(/^\/+|\/+$/g, '');
    } catch { /* Account usernames are allowed by the existing input contract. */ }
    return `${item.source_system}:${value.toLowerCase()}`;
}

export function businessSubjectDeclarationsMatch(request: MarketMonitorWrite, response: Record<string, unknown>): boolean {
    const matches = (expected: unknown, actual: unknown) => validBusinessSubject(actual)
        && (expected === null ? actual === null : record(expected) && record(actual)
            && normalizeBusinessName(String(expected.name)) === actual.name && expected.relationship === actual.relationship);
    if (Object.prototype.hasOwnProperty.call(request, 'business_subject') && !matches(request.business_subject, response.business_subject)) return false;
    for (const id of request.identifiers.filter(item => Object.prototype.hasOwnProperty.call(item, 'business_subject'))) {
        const candidates = (Array.isArray(response.identifiers) ? response.identifiers : []).filter(item => record(item)
            && item.kind === id.kind && typeof item.value === 'string' && item.source_system === id.source_system
            && businessIdentifierKey(item as unknown as MarketMonitorIdentifier) === businessIdentifierKey(id));
        if (candidates.length !== 1 || !matches(id.business_subject, candidates[0].business_subject)) return false;
    }
    return true;
}
